import { combineReducers, ReducersMapObject } from 'redux'
import { reducer as formReducer, FormState } from 'redux-form'
import { routerReducer, LocationChangeAction } from 'react-router-redux'

import { Action } from '../actions/types'

import * as Calc from './calculation'
import * as Visualization from './visualization'
import * as Project from './project'
import * as Tabulation from './tabulation'

export type LoginView = 'none' | 'select' | 'login' | 'signup' | 'registered'

export type UserData = {
  username: string
  fullName: string
  company: string
  country: string
}

export namespace Store {
  export type MainState = {
    readonly working: boolean
    readonly loginView: LoginView
    readonly email?: string

    readonly notificationMessage: string | null
  }

  export type UserState =
    | {
        readonly username: string
        readonly fullName: string
        readonly company: string
        readonly country: string
        readonly loggedIn: true
      }
    | { readonly loggedIn: false }

  export type Complete = {
    readonly ui: MainState
    readonly calc: Calc.CalculationState
    readonly vis: Visualization.VisualizationState
    readonly user: UserState
    readonly project: Project.ProjectState
    readonly tabulation: Tabulation.TabulationState
  }
}

const initialState: Store.MainState = {
  working: false,
  loginView: 'none',
  notificationMessage: null
}

const reducer = (
  state: Store.MainState = initialState,
  action: Action | LocationChangeAction
): Store.MainState => {
  switch (action.type) {
    case 'UI_WORKING':
      return { ...state, working: true }

    case 'UI_WORK_COMPLETE':
      return { ...state, working: false }

    case 'SHOW_NOTIFICATION':
      return { ...state, notificationMessage: action.payload }

    case 'HIDE_NOTIFICATION':
      return { ...state, notificationMessage: null }

    case 'HIDE_LOGIN_FORM':
      return { ...state, loginView: 'none' }

    case 'HIDE_SIGNUP_FORM':
      return { ...state, loginView: 'login' }

    case 'SIGNUP_NEXT_FORM':
      return { ...state, loginView: 'registered', email: action.payload }

    case 'HIDE_REGISTERED_FORM':
      return { ...state, loginView: 'login', email: action.payload }

    case 'SHOW_LOGIN_FORM':
      return { ...state, loginView: action.payload }

    case 'USER_LOGIN_COMPLETE':
      return { ...state, loginView: 'none' }

    case '@@router/LOCATION_CHANGE':
      return { ...state }

    default:
      return state
  }
}

const initialUserState: Store.UserState = {
  loggedIn: false
}

const userReducer = (
  state: Store.UserState = initialUserState,
  action: Action
): Store.UserState => {
  switch (action.type) {
    case 'USER_LOGIN_COMPLETE':
      return {
        username: action.payload.username,
        fullName: action.payload.fullName,
        company: action.payload.company,
        country: action.payload.country,
        loggedIn: true
      }

    case 'USER_LOGOUT':
      return {
        loggedIn: false
      }

    default:
      return state
  }
}

export function getNotificationMessage(state: Store.Complete): string | null {
  return state.ui.notificationMessage
}

export function getLoginFormVisible(state: Store.Complete): boolean {
  return state.ui.loginView !== 'none'
}

export function getLoginView(state: Store.Complete): LoginView {
  return state.ui.loginView
}

export function getUserLoggedIn(state: Store.Complete): boolean {
  return state.user.loggedIn
}

export type UserInfo =
  | {
      loggedIn: boolean
    } & UserData
  | null

export function getUserInfo(state: Store.Complete): UserInfo {
  return state.user.loggedIn ? state.user : null
}

function clearOnSelect(state: FormState, action: Action) {
  switch (action.type) {
    case 'CASE_SELECT_ITEM':
      if (state && state.values && state.values.id !== action.payload) {
        return undefined
      }
      return state

    case 'SELECT_CASE':
      return undefined

    default:
      return state
  }
}

export default function createReducer(asyncReducers: ReducersMapObject) {
  return combineReducers<Store.Complete>({
    ui: reducer,
    calc: Calc.default,
    vis: Visualization.default,
    project: Project.default,
    tabulation: Tabulation.default,
    form: formReducer.plugin({
      // TODO: move
      loads: clearOnSelect,
      slab: clearOnSelect
    }),
    router: routerReducer,
    user: userReducer,
    ...asyncReducers
  })
}



// WEBPACK FOOTER //
// ./src/reducers/index.ts